import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import api from '../../services/api';

interface Field {
  field: string;
  label: string;
  type: 'text' | 'number' | 'date';
  required: boolean;
}

interface FieldCategory {
  [category: string]: Field[];
}

interface DocumentFields {
  document_id: string;
  fields: FieldCategory;
  total_fields: number;
}

const DocumentFieldsPage: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [documentFields, setDocumentFields] = useState<DocumentFields | null>(null);
  const [fieldValues, setFieldValues] = useState<{ [key: string]: string }>({});
  const [documentTitle, setDocumentTitle] = useState('');

  useEffect(() => {
    if (documentId) {
      loadDocumentFields();
    }
  }, [documentId]);

  const loadDocumentFields = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post(`/ai-documents/document/${documentId}/extract-fields`);
      
      if (response.data.status === 'success') {
        setDocumentFields(response.data.data);
        
        // Inicializar valores dos campos
        const initialValues: { [key: string]: string } = {};
        Object.values(response.data.data.fields as FieldCategory).forEach((categoryFields: Field[]) => {
          categoryFields.forEach(field => {
            initialValues[field.field] = '';
          });
        });
        setFieldValues(initialValues);
      }
    } catch (err: any) {
      console.error('Erro ao carregar campos:', err);
      setError('Erro ao carregar campos do documento');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName: string, value: string) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleProcessFields = async () => {
    try {
      setProcessing(true);
      setError(null);
      
      const response = await api.post(`/ai-documents/document/${documentId}/process-fields`, {
        fields: fieldValues
      });
      
      if (response.data.status === 'success') {
        // Redirecionar para o editor
        navigate(`/documents/editor/${documentId}`);
      }
    } catch (err: any) {
      console.error('Erro ao processar campos:', err);
      setError('Erro ao processar campos');
    } finally {
      setProcessing(false);
    }
  };

  const getFieldValue = (fieldName: string) => {
    return fieldValues[fieldName] || '';
  };

  const isFormValid = () => {
    if (!documentFields) return false;
    
    const requiredFields = Object.values(documentFields.fields)
      .flat()
      .filter(field => field.required);
    
    return requiredFields.every(field => 
      fieldValues[field.field] && fieldValues[field.field].trim() !== ''
    );
  };

  const getTotalFilledFields = () => {
    return Object.values(fieldValues).filter(value => value && value.trim() !== '').length;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!documentFields) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">Nenhum campo encontrado no documento</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Preencher Campos do Documento
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Preencha os campos abaixo para personalizar seu documento jurídico.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
            <Chip 
              label={`${getTotalFilledFields()} / ${documentFields.total_fields} campos preenchidos`}
              color={getTotalFilledFields() === documentFields.total_fields ? 'success' : 'default'}
            />
            <Chip 
              label={`${Object.keys(documentFields.fields).length} categorias`}
              variant="outlined"
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            {Object.entries(documentFields.fields).map(([category, fields]) => (
              <Accordion key={category} defaultExpanded sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    {category}
                    <Chip 
                      label={`${fields.filter(f => fieldValues[f.field] && fieldValues[f.field].trim() !== '').length}/${fields.length}`}
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {fields.map((field) => (
                      <Grid item xs={12} md={6} key={field.field}>
                        <TextField
                          fullWidth
                          label={field.label}
                          type={field.type}
                          value={getFieldValue(field.field)}
                          onChange={(e) => handleFieldChange(field.field, e.target.value)}
                          required={field.required}
                          InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                          placeholder={
                            field.type === 'date' ? 'dd/mm/aaaa' :
                            field.type === 'number' ? 'Ex: 10.000,00' :
                            `Digite ${field.label.toLowerCase()}`
                          }
                          sx={{ mb: 2 }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/documents')}
            disabled={processing}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={processing ? <CircularProgress size={20} /> : <EditIcon />}
            onClick={handleProcessFields}
            disabled={processing || !isFormValid()}
            size="large"
          >
            {processing ? 'Processando...' : 'Continuar para Editor'}
          </Button>
        </Box>

        {!isFormValid() && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Preencha todos os campos obrigatórios para continuar.
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default DocumentFieldsPage; 